/**
 * Inbox cards the hub raises (apps/control `cards`): triage review, landing and ship taps,
 * BuildCards, doctor fixes, dispatch approvals, proposals, rules, blocked/stall cards.
 *
 * Every signed action (land.merge / land.deploy / doctor.fix / run.message) goes through
 * `Backend.dispatch`; risky kinds show the step-up prompt and take a passkey tap first.
 */
import { useNavigate } from '@tanstack/react-router'
import { type DeviceAlertReport, DIGEST_SLOT_LABEL, type DigestReport } from '@viko/protocol'
import {
  BuildCard as BuildCardUi,
  Button,
  Chip,
  type ItemState,
  type PipelineStep,
  Sheet,
} from '@viko/transcript-ui'
import {
  AlarmClock,
  ArrowUpRight,
  Check,
  CircleAlert,
  Columns2,
  GitMerge,
  KeyRound,
  ListChecks,
  Merge,
  Newspaper,
  Pencil,
  Play,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  Wrench,
  X,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { actionErrorText, actOnCard, cardOrigin, openTarget } from '../lib/inbox-actions.js'
import { useTask } from '../lib/queries.js'
import { stallReason } from '../lib/stall-text.js'
import type {
  BuildCard,
  CompareItem,
  InboxItem,
  PreparedCommand,
  TriageCard,
  TriageCardItem,
} from '../lib/types.js'
import { ComparePick } from './Artifacts.js'
import { DigestView } from './DigestView.js'
import { Ago, errorText, useToast } from './ui.js'

export type Resolve = (ids: string[]) => void

// ---- frame ----------------------------------------------------------------------------------

export function CardFrame({
  eyebrow,
  title,
  icon,
  tone = 'awaiting-user',
  createdAt,
  children,
  actions,
  onSnooze,
  label,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  icon?: ReactNode
  tone?: ItemState
  createdAt?: number
  children?: ReactNode
  actions?: ReactNode
  onSnooze?: () => void
  label?: string
}) {
  return (
    <article
      className={`vk-card vk-inbox vk-card--${tone} hubcard`}
      aria-label={label ?? (typeof title === 'string' ? title : undefined)}
    >
      <header className="vk-inbox__head">
        <div className="vk-inbox__ctx">
          {icon ? <span aria-hidden="true">{icon}</span> : null}
          {eyebrow}
        </div>
        {createdAt ? (
          <span className="vk-inbox__age">
            <Ago ts={createdAt} />
          </span>
        ) : null}
      </header>
      <h3 className="hubcard__title">{title}</h3>
      {children}
      <footer className="vk-inbox__foot">
        {onSnooze ? (
          <Button variant="ghost" size="sm" icon={<AlarmClock size={14} />} onClick={onSnooze}>
            Snooze
          </Button>
        ) : null}
        <span className="vk-grow" />
        {actions}
      </footer>
    </article>
  )
}

function Eyebrow({ item }: { item: InboxItem }) {
  return (
    <>
      <span className="vk-inbox__project">{item.projectName ?? 'Viko'}</span>
      {item.workstreamName ? (
        <>
          <span aria-hidden="true">›</span>
          <span className="vk-trunc">{item.workstreamName}</span>
        </>
      ) : null}
    </>
  )
}

const body = <T,>(i: InboxItem) => (i.body ?? {}) as T

/** Card actions shared by every card kind; derived cards never reach a hub endpoint. */
function useCardActions(item: InboxItem, resolve: Resolve) {
  const b = useBackend()
  const toast = useToast()
  const nav = useNavigate()
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true)
    try {
      await fn()
      resolve([item.id])
      if (ok) toast(ok)
      return true
    } catch (e) {
      toast(actionErrorText(e), 'error')
      return false
    } finally {
      setBusy(false)
    }
  }
  const cardId = item.cardId ?? item.id.replace(/^c:/, '')
  const target = openTarget(item)
  return {
    busy,
    run,
    cardId,
    derived: cardOrigin(item) === 'derived',
    /** Mark the card handled after its own work succeeded (best effort, never a hub call for derived ids). */
    finish: () => actOnCard(b.api, item, 'done').catch(() => {}),
    open: target ? () => void nav(target as never) : undefined,
    act: (
      action: 'approve' | 'dismiss' | 'done',
      extra: Record<string, unknown> = {},
      ok?: string,
    ) => run(() => actOnCard(b.api, item, action, extra), ok),
  }
}

// ---- dispatch per card type ---------------------------------------------------------------------

export function HubCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const cat = item.category ?? ''
  if (cat === 'device-alert')
    return <DeviceAlertCard item={item} resolve={resolve} onSnooze={onSnooze} />
  // ---- g13-digests ----
  if (cat === 'digest') return <DigestInboxCard item={item} resolve={resolve} onSnooze={onSnooze} />
  // ---- end g13-digests ----
  if (cat === 'usage-reset')
    return <UsageResetCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (item.type === 'land' && cat === 'ship-tap')
    return <ShipCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (item.type === 'build' || (item.kind === 'blocked' && cat === 'build-failed'))
    return <BuildInboxCard item={item} resolve={resolve} />
  const b0 = (item.body ?? {}) as Record<string, unknown>
  if (item.kind === 'decision' && cat === 'triage' && b0.card)
    return <TriageReview item={item} resolve={resolve} onSnooze={onSnooze} />
  if (item.kind === 'blocked' && cat === 'doctor')
    return <DoctorFixCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (b0.action === 'sign-run.message')
    return <SignMessageCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (item.kind === 'decision' && cat === 'grant')
    return <GrantNeededCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (cat === 'compare' && Array.isArray(b0.items))
    return <CompareCard item={item} resolve={resolve} onSnooze={onSnooze} />
  if (cat === 'rotation' && b0.command)
    return <RotationCard item={item} resolve={resolve} onSnooze={onSnooze} />
  return <GenericCard item={item} resolve={resolve} onSnooze={onSnooze} />
}

// ---- triage review ------------------------------------------------------------------------------

const ACTION_LABEL = (it: TriageCardItem) =>
  it.action.type === 'create_workstream'
    ? `New lane in ${it.projectName ?? 'a project'}`
    : it.action.type === 'add_task'
      ? 'Task in an existing workstream'
      : it.action.type === 'merge'
        ? 'Merge into existing work'
        : it.action.type === 'note'
          ? 'Keep as a note'
          : 'Skipped'

/**
 * One capture → N intents. Approve all is one tap; a suggestion chip re-routes an item (one
 * extra tap); Edit is the escape hatch (title/summary).
 */
export function TriageReview({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const toast = useToast()
  const { busy, run, cardId } = useCardActions(item, resolve)
  const [card, setCard] = useState<TriageCard>(body<{ card: TriageCard }>(item).card)
  const [editing, setEditing] = useState<TriageCardItem | null>(null)
  const op = async (o: Parameters<typeof b.api.cardOp>[1], final = false) => {
    try {
      const r = (await b.api.cardOp(cardId, o)) as { card?: TriageCard } | undefined
      if (r?.card) setCard(r.card)
      else {
        const fresh = await b.api.card(cardId).catch(() => undefined)
        const c = (fresh?.body as { card?: TriageCard } | undefined)?.card
        if (c) setCard(c)
      }
      if (final) resolve([item.id])
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const live = card.items.filter((i) => i.action.type !== 'skip')
  return (
    <CardFrame
      label="Review capture"
      icon={<Sparkles size={13} />}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            icon={<X size={14} />}
            disabled={busy}
            onClick={() => void run(() => b.api.cardOp(cardId, { op: 'dismiss' }), 'Dismissed')}
          >
            Dismiss
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Check size={14} />}
            disabled={busy || !live.length}
            onClick={() =>
              void run(
                () => b.api.cardOp(cardId, { op: 'approve_all' }),
                `Approved ${live.length} item${live.length === 1 ? '' : 's'}`,
              )
            }
          >
            Approve all
          </Button>
        </>
      }
    >
      <ul className="triage" aria-label="Intents">
        {card.items.map((it) => {
          const skipped = it.action.type === 'skip'
          return (
            <li key={it.intentId} className={`triage__item${skipped ? ' is-skipped' : ''}`}>
              <div className="triage__head">
                <span className="triage__title">{it.title}</span>
                <span className="vk-meta">{ACTION_LABEL(it)}</span>
              </div>
              {it.summary ? <p className="triage__summary">{it.summary}</p> : null}
              {it.flags.includes('possible_duplicate') ? (
                <p className="triage__flag">
                  <CircleAlert size={12} aria-hidden="true" /> Possible duplicate
                </p>
              ) : null}
              <div className="triage__actions">
                {!skipped
                  ? it.suggestions.map((sg) => (
                      <button
                        key={sg.targetId}
                        type="button"
                        className="vk-chip vk-chip--button"
                        title={sg.reason}
                        onClick={() =>
                          void op({ op: 'merge', intentId: it.intentId, targetId: sg.targetId })
                        }
                      >
                        <Merge size={12} aria-hidden="true" />
                        <span className="vk-chip__text">Merge into {sg.title}</span>
                      </button>
                    ))
                  : null}
                <button
                  type="button"
                  className="vk-chip vk-chip--button"
                  onClick={() => setEditing(it)}
                  aria-label={`Edit ${it.title}`}
                >
                  <Pencil size={12} aria-hidden="true" />
                  <span className="vk-chip__text">Edit</span>
                </button>
                <button
                  type="button"
                  className="vk-chip vk-chip--button"
                  onClick={() =>
                    void op(
                      skipped
                        ? { op: 'restore', intentId: it.intentId }
                        : { op: 'skip', intentId: it.intentId },
                    )
                  }
                >
                  <span className="vk-chip__text">{skipped ? 'Restore' : 'Skip'}</span>
                </button>
              </div>
            </li>
          )
        })}
      </ul>
      {editing ? (
        <EditIntent
          item={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await op({ op: 'edit', intentId: editing.intentId, patch })
            setEditing(null)
          }}
        />
      ) : null}
    </CardFrame>
  )
}

function EditIntent({
  item,
  onClose,
  onSave,
}: {
  item: TriageCardItem
  onClose: () => void
  onSave: (patch: { title: string; summary: string }) => Promise<void>
}) {
  const [title, setTitle] = useState(item.title)
  const [summary, setSummary] = useState(item.summary)
  return (
    <Sheet
      open
      onClose={onClose}
      title="Edit item"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void onSave({ title, summary })}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        <label className="field">
          <span>Title</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span>Summary</span>
          <textarea
            className="input"
            rows={4}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </label>
      </div>
    </Sheet>
  )
}

// ---- ship tap (land.deploy) -----------------------------------------------------------------------

function ShipCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const { busy, finish, run } = useCardActions(item, resolve)
  const s = body<{
    projectId?: string
    laneId?: string
    machineId?: string
    landings?: number
    production?: boolean
  }>(item)
  const ship = () =>
    run(async () => {
      if (!s.machineId || !s.projectId || !s.laneId)
        throw new Error('This ship request has no machine or lane.')
      await b.dispatch(
        'land.deploy',
        s.machineId,
        { projectId: s.projectId, lane: s.laneId },
        {
          summary: `Ship ${s.laneId}${s.landings ? ` (${s.landings} landings)` : ''}`,
          // passkey-budget WP3: listed and called out in the approval tray.
          ...(s.production ? { production: true } : {}),
        },
      )
      await finish()
    }, 'Shipping')
  return (
    <CardFrame
      label="Ship?"
      icon={<Rocket size={13} />}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <Button
          variant="primary"
          size="sm"
          icon={<KeyRound size={14} />}
          disabled={busy}
          onClick={() => void ship()}
        >
          Ship
        </Button>
      }
    >
      <p className="hubcard__text">
        {s.landings
          ? `${s.landings} landing${s.landings === 1 ? '' : 's'} waiting for the ${s.laneId} lane.`
          : `The ${s.laneId ?? 'release'} lane is waiting for your go.`}{' '}
        Shipping needs a passkey tap.
      </p>
    </CardFrame>
  )
}

// ---- builds -------------------------------------------------------------------------------------

export function buildState(status: string): ItemState {
  return status === 'available'
    ? 'ok'
    : status === 'failed'
      ? 'error'
      : status === 'queued'
        ? 'pending'
        : status === 'cancelled'
          ? 'cancelled'
          : 'streaming'
}

const BUILD_STEPS = [
  ['queued', 'Queue'],
  ['building', 'Build'],
  ['uploading', 'Upload'],
  ['processing', 'Process'],
  ['available', 'Available'],
] as const

export function buildSteps(status: string, failedAt?: string): PipelineStep[] {
  const idx = BUILD_STEPS.findIndex(([k]) => k === status)
  const failIdx =
    status === 'failed'
      ? Math.max(
          1,
          BUILD_STEPS.findIndex(([k]) => k === failedAt),
        )
      : -1
  return BUILD_STEPS.map(([id, label], i) => {
    let state: ItemState = 'pending'
    if (status === 'available') state = 'ok'
    else if (failIdx >= 0) state = i < failIdx ? 'ok' : i === failIdx ? 'error' : 'pending'
    else if (idx >= 0) state = i < idx ? 'ok' : i === idx ? 'streaming' : 'pending'
    return { id, label, state }
  })
}

export function buildNumber(c: Pick<BuildCard, 'title' | 'version'>): number {
  const m = /Build (\d+)/.exec(c.title)
  return m ? Number(m[1]) : Number(/\d+/.exec(c.version ?? '')?.[0] ?? 0)
}

/** BuildCard: pipeline pips, includes, feedback on this build, open links. */
export function BuildView({
  card,
  project,
  onFeedback,
}: {
  card: BuildCard
  project?: string
  onFeedback?: () => void
}) {
  const link = card.links[0]?.url
  return (
    <BuildCardUi
      number={buildNumber(card)}
      lane={card.laneId}
      {...(card.version ? { version: card.version } : {})}
      {...(project ? { project } : {})}
      state={buildState(card.status)}
      steps={buildSteps(card.status)}
      includes={card.tasks.map((t, i) => ({ id: t.taskId ?? `c${i}`, title: t.title }))}
      startedAt={card.createdAt ?? Date.now()}
      {...(card.failure
        ? {
            failure: {
              step: card.failure.category ?? 'build',
              log: [card.failure.cause, card.failure.fix, card.failure.evidence]
                .filter(Boolean)
                .join('\n'),
            },
          }
        : {})}
      {...(onFeedback ? { onFeedback } : {})}
      {...(link ? { onOpen: () => window.open(link, '_blank', 'noopener') } : {})}
    />
  )
}

function BuildInboxCard({ item, resolve }: { item: InboxItem; resolve: Resolve }) {
  const card = item.body as BuildCard | null
  const [feedback, setFeedback] = useState(false)
  const { act, derived, open } = useCardActions(item, resolve)
  if (!card?.buildId) return <GenericCard item={item} resolve={resolve} />
  return (
    <div className="grid gap-1.5">
      <BuildView
        card={card}
        {...(item.projectName ? { project: item.projectName } : {})}
        onFeedback={() => setFeedback(true)}
      />
      <div className="flex justify-end gap-1.5">
        {open ? (
          <Button variant="ghost" size="sm" icon={<ArrowUpRight size={14} />} onClick={open}>
            Open
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          title="Hide this build card. It returns only for a new build result."
          onClick={() => void act(derived ? 'dismiss' : 'done')}
        >
          {derived ? 'Dismiss' : 'Mark seen'}
        </Button>
      </div>
      {feedback ? <FeedbackSheet build={card} onClose={() => setFeedback(false)} /> : null}
    </div>
  )
}

/** "Feedback on this build" → POST /api/builds/:id/feedback (routed to the live run or a task). */
export function FeedbackSheet({ build, onClose }: { build: BuildCard; onClose: () => void }) {
  const b = useBackend()
  const toast = useToast()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const send = async () => {
    setBusy(true)
    try {
      const r = await b.api.buildFeedback(build.buildId, { text })
      toast(
        r.status === 'injected'
          ? 'Sent to the live session'
          : r.status === 'start-session' || r.taskId
            ? 'Queued as a task for this build'
            : 'Captured for triage',
      )
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title="Feedback on this build"
      subtitle={build.title}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !text.trim()} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send feedback'}
          </Button>
        </>
      }
    >
      <label className="field pb-2">
        <span>What did you notice?</span>
        <textarea
          className="input"
          rows={4}
          value={text}
          // biome-ignore lint/a11y/noAutofocus: the sheet exists to type into
          autoFocus
          placeholder="The jump feels floaty on level 3"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
    </Sheet>
  )
}

// ---- doctor fix ---------------------------------------------------------------------------------

function DoctorFixCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const { busy, finish, run, act } = useCardActions(item, resolve)
  const f = body<{
    checkId?: string
    machineId?: string
    problem?: string
    fix?: { text?: string; command?: string; autoFix?: boolean } | string
  }>(item)
  const fix = typeof f.fix === 'string' ? { text: f.fix } : (f.fix ?? {})
  return (
    <CardFrame
      label="Machine fix"
      icon={<Wrench size={13} />}
      eyebrow={<span className="vk-inbox__project">Machine</span>}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      tone="error"
      actions={
        <>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
            Dismiss
          </Button>
          {f.checkId && f.machineId ? (
            <Button
              variant="primary"
              size="sm"
              icon={<Wrench size={14} />}
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await b.dispatch('doctor.fix', f.machineId as string, {
                    checkId: f.checkId as string,
                  })
                  await finish()
                }, 'Fix sent to the machine')
              }
            >
              Fix
            </Button>
          ) : null}
        </>
      }
    >
      {f.problem ? <p className="hubcard__text">{f.problem}</p> : null}
      {fix.text ? <p className="hubcard__text text-text-2">Fix: {fix.text}</p> : null}
      {fix.command ? <code className="hubcard__code">{fix.command}</code> : null}
    </CardFrame>
  )
}

// ---- sign a message into a live run (capture/feedback without a grant) ---------------------------

function SignMessageCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const { busy, finish, run, act } = useCardActions(item, resolve)
  const s = body<{
    machineId?: string
    payload?: { runId: string; text: string; clientMessageId?: string }
  }>(item)
  return (
    <CardFrame
      icon={<ShieldCheck size={13} />}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
            Dismiss
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !s.machineId || !s.payload}
            onClick={() =>
              void run(async () => {
                await b.dispatch('run.message', s.machineId as string, {
                  ...(s.payload as { runId: string; text: string }),
                  queue: true,
                })
                await finish()
              }, 'Sent')
            }
          >
            Send
          </Button>
        </>
      }
    >
      {s.payload?.text ? (
        <blockquote className="hubcard__quote">{s.payload.text}</blockquote>
      ) : null}
    </CardFrame>
  )
}

// ---- grant needed ---------------------------------------------------------------------------------

function GrantNeededCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const nav = useNavigate()
  const { busy, act } = useCardActions(item, resolve)
  const g = body<{
    machines?: string[]
    projects?: string[]
    machineIds?: string[]
    projectIds?: string[]
  }>(item)
  const machines = g.machineIds ?? g.machines ?? []
  const projects = g.projectIds ?? g.projects ?? []
  return (
    <CardFrame
      icon={<KeyRound size={13} />}
      eyebrow={<span className="vk-inbox__project">Automation</span>}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              void nav({
                to: '/settings/advanced',
                hash: 'automation',
                search: { grantMachines: machines.join(','), grantProjects: projects.join(',') },
              })
            }
          >
            Review grant
          </Button>
        </>
      }
    >
      <p className="hubcard__text">
        Ready tasks are parked until you allow Viko to start runs on its own. A grant is bounded
        (projects, machines, rate, 7 days) and never covers landing, deploys or secrets.
      </p>
    </CardFrame>
  )
}

// ---- compare / pick ------------------------------------------------------------------------------

function CompareCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const { busy, act } = useCardActions(item, resolve)
  const c = body<{ items: CompareItem[]; prompt?: string }>(item)
  return (
    <CardFrame
      label="Compare"
      icon={<Columns2 size={13} />}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
          Dismiss
        </Button>
      }
    >
      <ComparePick
        cardId={item.cardId ?? item.id.replace(/^c:/, '')}
        items={c.items}
        title={c.prompt ?? 'Pick one per question'}
        onDone={() => resolve([item.id])}
      />
    </CardFrame>
  )
}

// ---- session rotation ------------------------------------------------------------------------------

function RotationCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const nav = useNavigate()
  const { busy, finish, run, act } = useCardActions(item, resolve)
  const r = body<{ text?: string; command: PreparedCommand }>(item)
  const newRun = (r.command.payload as { runId?: string }).runId
  return (
    <CardFrame
      label="Session rotation"
      icon={<RefreshCw size={13} />}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      onSnooze={onSnooze}
      actions={
        <>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
            Keep going
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Play size={14} />}
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await b.signPrepared(r.command, { summary: 'Start the rotated session' })
                await finish()
                if (newRun) void nav({ to: '/s/$runId', params: { runId: newRun } })
              }, 'Fresh session started')
            }
          >
            Start fresh session
          </Button>
        </>
      }
    >
      <p className="hubcard__text">
        {r.text ||
          'This session is long or its context is nearly full. A fresh session continues from a handoff with the same workstream and task.'}
      </p>
    </CardFrame>
  )
}

// ---- everything else --------------------------------------------------------------------------------

export function GenericCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze?: () => void
}) {
  const { busy, act, derived, open } = useCardActions(item, resolve)
  const cat = item.category ?? ''
  const b0 = (item.body ?? {}) as Record<string, unknown>
  const approvable =
    b0.action === 'approve-dispatch' ||
    item.kind === 'proposal' ||
    item.kind === 'rule' ||
    !!(b0.decision as { offer?: unknown } | undefined)?.offer ||
    !!b0.set
  const blocked = item.kind === 'blocked'
  const approveLabel =
    b0.action === 'approve-dispatch'
      ? 'Start them'
      : item.kind === 'proposal'
        ? 'Approve all'
        : item.kind === 'rule'
          ? 'Promote'
          : b0.set
            ? 'Apply proposals'
            : 'Approve'
  const icon =
    item.kind === 'proposal' ? (
      <ListChecks size={13} />
    ) : item.kind === 'landing' ? (
      <GitMerge size={13} />
    ) : blocked ? (
      <CircleAlert size={13} />
    ) : (
      <Sparkles size={13} />
    )
  const detail =
    (typeof b0.error === 'string' && b0.error) ||
    (typeof b0.detail === 'string' && b0.detail) ||
    (typeof (b0.failure as { cause?: string } | undefined)?.cause === 'string' &&
      (b0.failure as { cause: string }).cause) ||
    undefined
  const items = Array.isArray(b0.items) ? (b0.items as { title?: string; taskId?: string }[]) : []
  return (
    <CardFrame
      icon={icon}
      eyebrow={<Eyebrow item={item} />}
      title={item.title}
      createdAt={item.createdAt}
      tone={blocked ? 'error' : 'awaiting-user'}
      {...(onSnooze ? { onSnooze } : {})}
      actions={
        <>
          {open ? (
            <Button variant="ghost" size="sm" icon={<ArrowUpRight size={14} />} onClick={open}>
              Open
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            title="Hide this card without resolving it. It comes back only if a new problem is reported."
            onClick={() => void act('dismiss')}
          >
            Dismiss
          </Button>
          {approvable ? (
            <>
              {item.kind === 'proposal' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  title="Also approve future follow-ups in this workstream"
                  onClick={() =>
                    void act('approve', { standing: true }, 'Approved, and future ones too')
                  }
                >
                  Always
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                icon={<Check size={14} />}
                disabled={busy}
                onClick={() => void act('approve', {}, 'Approved')}
              >
                {approveLabel}
              </Button>
            </>
          ) : derived ? null : (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              title="Mark this resolved: the problem is handled."
              onClick={() => void act('done', {}, 'Done')}
            >
              Done
            </Button>
          )}
        </>
      }
    >
      {/* inbox-detail: plain-language reasons only; an unknown stall cause gets no chip */}
      {cat && (!cat.startsWith('stall:') || stallReason(cat)) ? (
        <div>
          <Chip tone={blocked ? 'fail' : undefined}>
            {cat.startsWith('stall:') ? stallReason(cat) : cat}
          </Chip>
        </div>
      ) : null}
      {detail ? <p className="hubcard__text">{detail}</p> : null}
      {items.length ? (
        <ul className="hubcard__list">
          {items.slice(0, 6).map((t, i) => (
            <li key={t.taskId ?? i}>{t.title ?? t.taskId}</li>
          ))}
        </ul>
      ) : null}
    </CardFrame>
  )
}

function UsageTaskName({ id }: { id: string }) {
  const task = useTask(id)
  return <li>{task.data?.title ?? id}</li>
}

/** Content comes from the client's task data; card bodies contain routing metadata only. */
export function UsageResetCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const b = useBackend()
  const { busy, finish, run, act, cardId } = useCardActions(item, resolve)
  const data = body<{ items: { taskId: string }[] }>(item)
  return (
    <CardFrame
      title={item.title}
      eyebrow="Capacity before reset"
      onSnooze={onSnooze}
      actions={
        <>
          <Button variant="ghost" disabled={busy} onClick={() => void act('dismiss')}>
            Dismiss
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const assignments = await b.api.prepareUsageCard(cardId)
                for (const a of assignments) {
                  const task = await b.api.task(a.taskId)
                  const prompt = [
                    `Task [${task.id}]: ${task.title}`,
                    task.description,
                    ...task.acceptance.map((t) => `- ${t}`),
                    'Use viko_context for the workstream brief and rules.',
                  ]
                    .filter(Boolean)
                    .join('\n\n')
                  await b.dispatch('run.start', a.machineId, {
                    runId: a.runId,
                    agent: a.agent,
                    taskId: task.id,
                    projectId: task.projectId,
                    ...(task.workstreamId ? { workstreamId: task.workstreamId } : {}),
                    prompt,
                  })
                }
                await finish()
              }, 'Approved tasks started')
            }
          >
            Run tasks
          </Button>
        </>
      }
    >
      <ul className="hubcard__list">
        {data.items.map((t) => (
          <UsageTaskName key={t.taskId} id={t.taskId} />
        ))}
      </ul>
    </CardFrame>
  )
}

// ---- g2g3-alerts: battery / disk / usage-limit alert card ------------------------------------------

const fmtBytes = (n: number) =>
  n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GiB` : `${Math.round(n / 1024 ** 2)} MiB`
const fmtTs = (ms: number) => new Date(ms).toLocaleString()

/**
 * Decrypted alert (never from the hub). Every number is shown in full in the drill-down;
 * remedies that change anything are labelled as needing consent, nothing runs from here.
 */
export function DeviceAlertCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const { busy, act, open } = useCardActions(item, resolve)
  const a = item.body as DeviceAlertReport
  const m = a.metric
  const value = m ? (m.unit === 'bytes' ? fmtBytes(m.value) : `${m.value}%`) : undefined
  const threshold = m ? (m.unit === 'bytes' ? fmtBytes(m.threshold) : `${m.threshold}%`) : undefined
  return (
    <CardFrame
      label={a.title}
      icon={<CircleAlert size={13} />}
      eyebrow={
        <span className="vk-inbox__project">
          {a.alertKind === 'battery'
            ? 'Battery'
            : a.alertKind === 'disk'
              ? 'Disk space'
              : a.alertKind === 'permission'
                ? 'macOS permission'
                : a.alertKind === 'wait'
                  ? 'Long job'
                  : 'Usage limit'}
        </span>
      }
      title={a.title}
      createdAt={item.createdAt}
      tone={a.level === 'critical' ? 'error' : 'awaiting-user'}
      onSnooze={onSnooze}
      actions={
        <>
          {open ? (
            <Button variant="ghost" size="sm" icon={<ArrowUpRight size={14} />} onClick={open}>
              Open
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            title="Hide this alert. It clears itself when the condition ends and returns if it is raised again."
            onClick={() => void act('dismiss')}
          >
            Dismiss
          </Button>
        </>
      }
    >
      <p className="hubcard__text">{a.detail}</p>
      {a.parked ? <p className="hubcard__text">New runs are held while this is active.</p> : null}
      {a.remedies.length ? (
        <ul className="hubcard__list" aria-label="Suggested actions">
          {a.remedies.map((r) => (
            <li key={r.id}>
              {r.label}
              {r.needsConsent ? ' (asks for your approval first)' : ''}
            </li>
          ))}
        </ul>
      ) : null}
      <details>
        <summary>Details</summary>
        <dl className="hubcard__text">
          {value ? (
            <>
              <dt>Current</dt>
              <dd>{value}</dd>
              <dt>Alert threshold</dt>
              <dd>{threshold}</dd>
            </>
          ) : null}
          {m?.volume ? (
            <>
              <dt>Volume</dt>
              <dd>{m.volume}</dd>
            </>
          ) : null}
          {m?.account ? (
            <>
              <dt>Account</dt>
              <dd>{m.account}</dd>
            </>
          ) : null}
          {m?.window ? (
            <>
              <dt>Window</dt>
              <dd>{m.window}</dd>
            </>
          ) : null}
          {a.forecast ? (
            <>
              <dt>Forecast exhaustion</dt>
              <dd>{fmtTs(a.forecast.exhaustsAt)}</dd>
              {a.forecast.resetsAt ? (
                <>
                  <dt>Window resets</dt>
                  <dd>{fmtTs(a.forecast.resetsAt)}</dd>
                </>
              ) : null}
            </>
          ) : null}
          <dt>Raised</dt>
          <dd>{fmtTs(a.raisedAt)}</dd>
        </dl>
      </details>
    </CardFrame>
  )
}
// ---- end g2g3-alerts ----

// ---- g13-digests: morning / afternoon / evening digest (FYI; decrypted report) ----
export function DigestInboxCard({
  item,
  resolve,
  onSnooze,
}: {
  item: InboxItem
  resolve: Resolve
  onSnooze: () => void
}) {
  const { busy, act } = useCardActions(item, resolve)
  const d = item.body as DigestReport
  return (
    <CardFrame
      label={`${DIGEST_SLOT_LABEL[d.slot]} digest`}
      icon={<Newspaper size={13} />}
      eyebrow={<span className="vk-inbox__project">{item.projectName ?? 'Digest'}</span>}
      title={`${DIGEST_SLOT_LABEL[d.slot]} digest: ${d.headline}`}
      createdAt={item.createdAt}
      tone="ok"
      onSnooze={onSnooze}
      actions={
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act('dismiss')}>
          Dismiss
        </Button>
      }
    >
      <DigestView digest={d} perSection={3} />
    </CardFrame>
  )
}
// ---- end g13-digests ----
